// MenuPanel / MenuPanelItem — THE house dropdown recipe (extracted from the
// /files context menu per Nika: "reuse this contextmenu style, shape and item
// styles to all other dropdowns").
//
// Panel: the `dialog` overlay surface + the `overlay` elevation token
// (DESIGN_LANGUAGE.md §2 — menus are overlay-level chrome; 2026-06-10 per
// Nika "remove that overlayStrong background from contextmenu and use
// default styles"); radiusCard shell, Y padding.
// Item:  borderless button row — Y/Z1 padding, radiusControl, title text,
//        the house `hover` wash (same pairing as the brand DropdownItem).
//
// Consumers extend these and add their own positioning/children. NOTE: no
// `transition` on backgrounds here — the runtime re-applies background per
// tick on some rows, perpetually restarting CSS transitions (see the ticket
// in tickets/smbls.md), which makes hover styles appear dead.

export const MenuPanel = {
  flow: 'y',
  gap: '0',
  minWidth: 'E1',
  maxWidth: '100%',
  maxHeight: '60dvh',
  overflow: 'auto',
  // Don't chain the panel's edge-bounce to the surface behind it (iPad program
  // P2 — overscroll-contain on inner scrollers; guideline MUST for overlays).
  overscrollBehavior: 'contain',
  padding: 'Y',
  round: 'radiusCard',
  theme: 'dialog',
  shadow: 'overlay',
  borderWidth: '0',
  boxSizing: 'border-box',
  '@mobileL': {
    minWidth: '0',
    maxWidth: 'calc(100dvw - var(--spacing-B))'
  },
  role: 'menu',

  // THE MENU KEYBOARD MODEL on every house menu (functions/menuKeyboard.js;
  // found missing by crm-w1 on a module popover, 2026-09-26): ArrowUp /
  // ArrowDown wrap through the rows, Home / End reach the ends. Rows are
  // <button>s, so Enter / Space activate natively; Escape belongs to the host
  // (the dropdown layer closes on it and returns focus to the trigger;
  // ContextDropdown and the other self-hosted menus keep their own handler,
  // which replaces this one). The panel is a programmatic focus target only.
  tabindex: '-1',
  onKeydown: (e, el) => el.call('menuArrowNav', e, el.node),
  // Focus lands INSIDE a menu the dropdown layer hosts
  // (`openDropdown({ content: { extends: 'MenuPanel', … } })` — every module
  // popover): the layer mounts the panel only while it is open, so its render
  // IS the open edge. Keyed on the host, never on mount alone — a MenuPanel
  // that sits on a page (a catalog specimen, a sidebar list) must not pull the
  // page's focus into itself.
  onRender: (el) => {
    if (typeof el.lookup !== 'function' || !el.lookup('DropdownRoot')) return
    el.call('armMenuFocus', el, { when: (node) => el.call('menuNodeVisible', node) })
  },
  // A focus CONTAINER, not a control: the brand reset draws the focus ring as
  // a box-shadow on every [tabindex]:focus-visible, which REPLACED the panel's
  // elevation with a bare ring whenever the panel itself held focus. The
  // panel keeps its `overlay` elevation; the focused row carries the ring.
  ':focus-visible': { boxShadow: 'var(--shadow-overlay)' }
}

// THE ROW SLOTS (white-label redesign 2026-09-26, DESIGN_SPEC §5.9; r004
// r013 r025) — all optional, declared here so no menu hand-builds them:
//
//   MenuPanelItem: {
//     text: '{{ crm.deal.edit | polyglot }}',
//     Icon: { name: 'edit' },              // leading glyph (drawn first)
//     Shortcut: {                          // trailing keycaps — ONLY for a
//       AppKeycap: { text: '⌘' },         // shortcut that is wired
//       AppKeycap_1: { text: 'E' }
//     },
//     ariaKeyshortcuts: 'Meta+E',          // …and name it to AT on the row
//     danger: true,                        // the destructive row
//     checked: (el, s) => s.root.x === 'a' // a current-value row: the check
//   }
//
// The label is the row's own text node; the Icon is ordered before it, the
// Shortcut and the Check are pushed to the far end. A row with `checked`
// declared is a `menuitemcheckbox` with aria-checked and keeps its check
// glyph's box whether checked or not (Rule 66); any other row is a
// `menuitem`. `danger` paints the row in `dangerInk`; under the pointer it
// takes the brand error fill pair — never a raw red. On the highlighted row (hover or
// keyboard focus) the keycaps LIFT onto the `raised` fill + `float`, the way
// r004 shows them.
export const MenuPanelItem = {
  // The glyph plate's state rules (hover, keyboard focus invert it); they
  // match nothing on a row without a plate. The checked row's inversion is
  // below, beside `aria-checked`.
  extends: 'GlyphPlateHolder',
  tag: 'button',
  type: 'button',
  width: '100%',
  display: 'flex',
  flow: 'x',
  align: 'center left',
  padding: 'Y Z1',
  round: 'radiusControl',
  fontSize: 'Z1',
  textAlign: 'left',
  // THE TEXT LAW (overflow law §3b.4 + .8): a row's label wraps inside the
  // panel — a long email, URL or Georgian compound never widens the menu past
  // the viewport or spills out of the row (it was `nowrap`).
  whiteSpace: 'normal',
  overflowWrap: 'anywhere',
  cursor: 'pointer',
  borderWidth: '0',
  background: 'transparent',
  color: 'title',
  role: (el) => (el.checked === undefined ? 'menuitem' : 'menuitemcheckbox'),
  ariaChecked: (el) => (el.checked === undefined ? null : el.checked ? 'true' : 'false'),
  // The state hooks below are FLAT `dataX` props (→ `data-x`), never a
  // `data: {}` block: a call-site `data` object REPLACES the component's
  // whole `data` on this runtime (FW-ELEMENT-DEFINITION-DATA-REPLACES-
  // EXTENDS-DATA-1), which silently dropped `data-danger` from a row that
  // also carried its own `data`. A flat key is only replaced by the same key.
  dataDanger: (el) => !!el.danger,
  // iPad/touch: 44pt min row height (Apple HIG). The Y/Z1 padding alone yields
  // ~31px rows — too small to tap reliably. Coarse-pointer only (no-op on
  // desktop). Fixed-px token on purpose: the old `minHeight: 'C'` resolved
  // through the em chain at this row's Z1 font-size to ~40px, not 44.
  '@coarse': { minHeight: 'controlH' },
  '@mobileL': { minHeight: 'controlH' },
  touchAction: 'manipulation',
  // Hover: the house `hover` wash token (adaptive pair — DESIGN_LANGUAGE.md
  // §4.3; byte-identical to the old raw 0.07/0.05 pair). Must stay a FLAT
  // prop: a `:hover` block containing ONLY `@dark`/`@light` sub-blocks emits
  // NOTHING through css-in-props — the rows rendered with zero hover rules
  // until a flat prop made the rule materialize. (2026-06-15 per Nika "those
  // items in dropdown do not have hover".)
  ':hover': { background: 'hover' },
  // Keyboard focus: THE house ring (DESIGN_LANGUAGE §8.2 — one ring,
  // globally), drawn INWARD via the `focusRingInset` shadow token because
  // this panel is `overflow: auto` and an outer spread would be clipped at
  // its edge. The row is a real <button>, so brand reset.js paints the outer
  // ring by selector; this class rule (higher specificity) swaps it for the
  // inset variant — same colours, same 1px gap + 2px ring.
  ':focus-visible': { shadow: 'focusRingInset' },
  // Press: one step deeper than hover, same adaptive family (§4.3) — a
  // click reads as contact, not just a lingering hover.
  ':active': { background: 'selected' },
  '&[data-danger]': { color: 'dangerInk' },
  // Hover and press take the brand ERROR FILL pair, never a tint of the row's
  // own ink: on the `raised` ground a menu takes inside a dialog (gray5), a
  // light danger ink tinting the ground lightened it under the text (hover
  // 3.62:1, press 3.30:1 measured). The fill pair holds 5.4:1 on raised,
  // 5.1:1 on the dialog, 6.4:1 in light (__tests__/dangerRowOnRaised.test.js).
  '&[data-danger]:hover': { background: 'errorFillBg', color: 'errorFillInk' },
  '&[data-danger]:active': { background: 'errorFillBg', color: 'errorFillInk' },
  // The keycaps lift on the highlighted row — two rules, so the keyboard one
  // is not wrapped in the engine's `@media (hover: hover)` guard with the
  // pointer one.
  '&:hover [data-keycap]': {
    background: 'raised',
    color: 'title',
    boxShadow: 'var(--shadow-float)'
  },
  '&:focus-visible [data-keycap]': {
    background: 'raised',
    color: 'title',
    boxShadow: 'var(--shadow-float)'
  },

  // Not rendered at all when the row names no glyph: a mounted Icon with no
  // `name` resolves its KEY as the icon name and warns `Unknown icon name
  // "Icon"` on every plain row (measured by lane W2). A row that should
  // gain a glyph later declares it up front and toggles it with `hide:`.
  // `hide:` stays beside `if:` as the fallback for a runtime whose `if:` is
  // inert (published element builds before 3.14.788 — ContextDropdown's
  // Icon slot carries the same pair).
  Icon: {
    if: (el) => !!el.name,
    hide: (el) => !el.name,
    order: '-1',
    flexShrink: '0',
    marginRight: 'Y1',
    color: 'currentColor'
  },

  // THE GLYPH PLATE (dropdowns spec §4 menu row): a row that names `glyph`
  // (a LINE glyph, e.g. 'lnAssistant', or a PIXEL one at its 16 px, e.g. 'pxFly') leads with the glyph language's plate
  // instead of a bare Icon — 24 px (`glyphPlateXs`, the navbar tab's plate),
  // the hairline ring at rest, inverted with the row (GlyphPlateHolder +
  // the checked rule below). Same spacing contract as the Icon slot (order
  // -1, no shrink, Y1 to the label). No `glyph`, no node.
  // (__tests__/menuPanelItemPlate.test.js)
  //
  // The glyph travels ONE hop at a time: the row's `glyph` onto the Plate's
  // own `glyph` prop, the Plate's onto its Icon. The Icon built-in calls its
  // `name` factory RAW on every run of its render effect, so the old
  // two-parent read (`el.parent.parent.glyph`) threw `Cannot read properties
  // of null (reading 'glyph')` once the Plate's link to its row was gone
  // while the Icon's effect still ran (prod console since c24d97aa8). A
  // custom prop on the Plate is wrapped by the framework into a getter before
  // the Plate's children exist; the getter returns undefined on a throw, so
  // the Icon's read can never throw.
  // (__tests__/menuPanelItemPlateGlyphRead.test.js)
  Plate: {
    if: (el) => !!el.parent.glyph,
    hide: (el) => !el.parent.glyph,
    extends: 'AppGlyphPlate',
    glyph: (el) => el.parent.glyph,
    ariaHidden: 'true',
    order: '-1',
    flexShrink: '0',
    marginRight: 'Y1',
    boxSize: 'glyphPlateXs',
    minWidth: 'glyphPlateXs',
    Icon: { name: (el) => el.parent.glyph, boxSize: 'iconSm' }
  },
  '&[aria-checked="true"] [data-glyph-plate]': {
    background: 'highlight',
    color: 'title-reversed',
    borderColor: 'highlight'
  },

  // Takes no box on a row without keycaps.
  Shortcut: {
    hide: (el) =>
      !(el.children || Object.keys(el).some((k) => k.charCodeAt(0) >= 65 && k.charCodeAt(0) <= 90)),
    display: 'flex',
    flow: 'x',
    gap: 'W',
    flexShrink: '0',
    marginLeft: 'auto',
    paddingLeft: 'A',
    ariaHidden: 'true'
  },

  Icon_check: {
    name: 'checkmark',
    hide: (el) => el.parent.checked === undefined,
    visibility: (el) => (el.parent.checked ? 'visible' : 'hidden'),
    flexShrink: '0',
    marginLeft: 'auto',
    paddingLeft: 'Z',
    boxSizing: 'content-box',
    ariaHidden: 'true',
    color: 'currentColor'
  }
}
